import React, { FC, useEffect, useMemo } from 'react'

import shortenAddress from '@ambire-common/utils/shortenAddress'
import BaseAddress from '@common/components/HumanizerAddress/components/BaseAddress'
import { Props as TextProps } from '@common/components/Text'
import useReverseLookup from '@common/hooks/useReverseLookup'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useContractNamesControllerState from '@web/hooks/useContractNamesController/useContractNamesController'

interface Props extends TextProps {
  address: string
  chainId: bigint
}

const AddressName: FC<Props> = ({ address, chainId, ...rest }) => {
  // Intentionally ignore reverseLookup's `isLoading`: on chains without an ETH
  // reverse-resolution provider (e.g. Unichain) it never flips to false, so
  // gating the render on it left a permanent spinner. Show a stable shortened
  // address immediately and upgrade to the ens/contract name once it resolves.
  const { ens } = useReverseLookup({ address })
  const { contractNames } = useContractNamesControllerState()
  const { dispatch } = useBackgroundService()

  const contract = useMemo(() => {
    return contractNames[address]
  }, [address, contractNames])

  const contractName = useMemo(() => {
    return contract?.name
  }, [contract])

  useEffect(() => {
    if (contractName) return

    dispatch({
      type: 'CONTRACT_NAMES_CONTROLLER_GET_NAME',
      params: { address, chainId }
    })
  }, [dispatch, address, chainId, contractName])

  return (
    <BaseAddress address={address} {...rest}>
      {/* Shorten the raw-address fallback (0x123456…abcdef) so a full 42-char
          address can't overflow and break the row layout on narrow/mobile
          screens; resolved names render in full. */}
      {ens || contractName || shortenAddress(address, 18, 6)}
    </BaseAddress>
  )
}

export default React.memo(AddressName)
